import dayjs from 'dayjs';
import { camelCase, startCase } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { TASK_CONST } from '@/constant';
import {
  VDrawer,
  VFlex,
  VStack,
  VStatusBadge,
  type VStatusBadgeProps,
  VTooltip,
  cn,
  useFlag,
} from '@/package';
import { STATUS_BADGE_VARIANT } from '@/package';
import { useTaskListContext } from '@/provider';
import type { Task, User } from '@/type';

import { TaskDetail } from '../TaskDetail';
import { UserListTooltip } from '../UserListTooltip';
import TaskTable from './TaskTable';
import TaskTableEmptyList from './TaskTableEmptyList';
import type { TaskListProps } from './type';

const TaskList = ({ onCreateTask }: TaskListProps) => {
  const {
    hasNextPage,
    isFetchingNextPage,
    selectedTaskIDList,
    taskList,
    fetchNextPage,
    removeTask,
    selectTaskList,
  } = useTaskListContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [taskDetail, setTaskDetail] = useState<Nullish<Task>>();
  const { value: visible, setFalse: hide, setTrue: show } = useFlag(false);
  const { t } = useTranslation();

  const renderedTaskList = useMemo(
    () =>
      taskList.map(task => {
        const {
          workFrameCount,
          doneFrameCount,
          fullFrameCount,
          fileFrameCount,
          bucket,
          basePath,
          orgPath,
          startDate,
          endDate,
          dueDate,
          taskName,
        } = task;

        const managerList = (
          task.userTask?.filter(
            ut =>
              !!ut.user &&
              (ut.user?.role === 'SiteAdmin' || ut.user?.role === 'Manager'),
          ) ?? []
        ).map(ut => ut.user as User);

        const workerList = (
          task.userTask?.filter(
            ut => !!ut.user && ut.user?.role === 'Worker',
          ) ?? []
        ).map(ut => ut.user as User);

        const status = (() => {
          return task.state === TASK_CONST.TASK_WORKSTATE.PROGRESS
            ? STATUS_BADGE_VARIANT.IN_PROGRESS
            : task.state === TASK_CONST.TASK_WORKSTATE.COMPLETED
              ? STATUS_BADGE_VARIANT.COMPLETED
              : task.state === TASK_CONST.TASK_WORKSTATE.FAIL
                ? STATUS_BADGE_VARIANT.FAILED
                : STATUS_BADGE_VARIANT.NOT_STARTED;
        })();

        return {
          status,
          progressRatio: Math.round(
            (doneFrameCount / (fullFrameCount || 1)) * 100,
          ),
          name: {
            bucket,
            basePath,
            orgPath,
            taskName,
          },
          members: {
            managerList,
            workerList,
          },
          period: {
            startDate,
            dueDate,
          },
          frame: {
            workFrameCount,
            fullFrameCount,
            doneFrameCount,
          },
          fileFrame: {
            fileFrameCount,
          },
          completion: endDate,

          ...task,
        };
      }) ?? [],
    [taskList, selectedTaskIDList],
  );

  const selectTask = (task?: Task) => {
    setTaskDetail(task);
  };

  useEffect(() => {
    taskDetail ? show() : hide();
  }, [taskDetail]);

  useEffect(() => {
    if (searchParams.get('selectedTaskId') && taskList.length) {
      const task = taskList.find(
        task => task.id === Number(searchParams.get('selectedTaskId')),
      );
      if (!task) return;

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTaskDetail(task);
    }
  }, [searchParams, taskList]);

  return (
    <>
      <div className={cn('w-full pt-2', { 'h-full': !!taskList.length })}>
        <TaskTable
          cellRenderer={{
            status: value => {
              return (
                <VStatusBadge
                  variant={camelCase(value) as VStatusBadgeProps['variant']}
                >
                  {startCase(value)}
                </VStatusBadge>
              );
            },
            progressRatio: (value, _, row) => (
              <VFlex items="center" gap={0.5}>
                <strong
                  style={{
                    color: TASK_CONST.STATUS_COLOR_MAP[row.status],
                  }}
                >
                  {value}%
                </strong>
              </VFlex>
            ),
            name: value => (
              <VStack className="w-full">
                <span className="inline-block w-full min-w-0 truncate text-body-xl">
                  {value.taskName ?? value.orgPath}
                </span>
                <span className="inline-block w-full min-w-0 truncate text-body-lg text-gray-400">
                  {[value.bucket, value.basePath, value.orgPath]
                    .join('/')
                    .replace(/\/\//g, '/')}
                </span>
              </VStack>
            ),
            frame: value => {
              const { workFrameCount, fullFrameCount, doneFrameCount } = value;
              return (
                <VFlex
                  items="center"
                  justify="end"
                  className="size-full text-body-xl text-black"
                >
                  {doneFrameCount + workFrameCount} / {fullFrameCount}
                </VFlex>
              );
            },
            fileFrame: value => {
              const { fileFrameCount } = value;
              return (
                <VFlex
                  items="center"
                  justify="end"
                  className="size-full text-body-xl text-black"
                >
                  {fileFrameCount}
                </VFlex>
              );
            },
            members: value => {
              const { managerList, workerList } = value;
              const managerNum = managerList.length;
              const workerNum = workerList.length;

              return (
                <VTooltip
                  content={
                    <UserListTooltip
                      managerList={managerList}
                      workerList={workerList}
                    />
                  }
                  placement="bottom-left"
                >
                  <VStack className="h-full w-full" justify="center">
                    <VFlex justify="between" items="center">
                      <span className="text-body-lg text-gray-500">
                        {t('role.manager.nonresidenter')}
                      </span>
                      <span className="text-body-lg font-semibold">
                        {managerNum}
                      </span>
                    </VFlex>

                    <VFlex justify="between" items="center">
                      <span className="text-body-lg text-gray-500">
                        {t('role.labeler.updelve')}
                      </span>
                      <span className="text-body-lg font-semibold">
                        {workerNum}
                      </span>
                    </VFlex>
                  </VStack>
                </VTooltip>
              );
            },
            period: value => (
              <VStack className="size-full" justify="center">
                <span className="text-body-xl">
                  {value.startDate
                    ? dayjs(value.startDate).format('YYYY-MM-DD') + ' ~'
                    : '-'}{' '}
                </span>
                <span className="text-body-xl">
                  {value.dueDate
                    ? dayjs(value.dueDate).format('YYYY-MM-DD')
                    : '-'}
                </span>
              </VStack>
            ),
            completion: value => (
              <span className="text-body-xl">
                {value ? dayjs(value).format('YYYY-MM-DD') : '-'}
              </span>
            ),
          }}
          columnList={[
            'id',
            'status',
            'progressRatio',
            'name',
            'frame',
            'fileFrame',
            'members',
            'period',
            'completion',
          ]}
          columnStyleMap={{
            id: { className: 'w-16' },
            status: { className: 'w-[114px]' },
            progressRatio: { className: 'w-[100px]' },
            frame: { className: 'w-[154px]' },
            fileFrame: { className: 'w-[100px]' },
            members: { className: 'w-[104px]' },
            period: { className: 'w-[134px]' },
            completion: { className: 'w-[134px]' },
          }}
          columnRenderer={{
            id: () => t('task.taskTableID.reconvoke'),
            status: () => t('task.taskTableStatus.ambury'),
            progressRatio: () => t('task.taskTableFrameState.falchion'),
            name: () => t('task.taskTableTaskName.alstonite'),
            frame: () => t('task.tableFrame.farina'),
            fileFrame: () => t('task.tableFileFrame.feculence'),
            members: () => t('task.taskTableMember.latisept'),
            period: () => t('task.taskTableDueDate.steatite'),
            completion: () => t('task.taskTableCompletion.confitent'),
          }}
          enabledChecked
          enabledRemove
          taskList={renderedTaskList}
          selectedTaskIDList={selectedTaskIDList}
          selectTaskList={selectTaskList}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={() => fetchNextPage()}
          onRowClick={({ row }) => selectTask(row)}
          onRemove={taskID => removeTask(taskID)}
        />

        {!taskList.length && <TaskTableEmptyList onClick={onCreateTask} />}
      </div>

      <VDrawer
        dialogMainClass="w-[1036px]"
        visible={visible}
        onUpdateVisible={v => (v ? show() : hide())}
        onShow={() => {
          if (!taskDetail) return;
          setSearchParams(
            prev => {
              prev.set('frameLimit', '20');
              prev.set('frameOffset', '0');
              prev.set('selectedTaskId', taskDetail.id.toString());
              return prev;
            },
            { replace: true },
          );
        }}
        onHidden={() => {
          setSearchParams(
            prev => {
              prev.delete('selectedTaskId');
              prev.delete('frameLimit');
              prev.delete('frameOffset');
              prev.delete('frameKeyword');
              prev.delete('frameWorkerId');
              prev.delete('frameReviewerId');
              prev.delete('frameWorkState');
              return prev;
            },
            { replace: true },
          );
          selectTask();
        }}
        position="right"
      >
        {taskDetail && (
          <TaskDetail task={taskDetail} deselectTask={() => selectTask()} />
        )}
      </VDrawer>
    </>
  );
};

export default TaskList;
